import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Zip_Code_Api } from "../../services/zip_code_api/zip_code_api";
import { setupAPIclient } from "../../services/setupAPIclientAuth";
import LocalStorage from "../../helpers/localstorage";
import trackEpiLogo from "../../assets/landing/track-epi-logo.jpeg";

interface FormData {
  name: string;
  email: string;
  address: string;
  neighborhood: string;
  postal_code: string;
  cnpj: string;
  city: string;
  state: string;
  password: string;
  access_permission_request: boolean;
  submaster_logo_url: string;
  number_house: string;
  fantasy_name:string;
  username: string;

}

const defaultFormData: FormData = {
  name: "",
  email: "",
  address: "",
  neighborhood: "",
  postal_code: "",
  cnpj: "",
  city: "",
  state: "",
  password: "",
  access_permission_request: false,
  submaster_logo_url: "",
  number_house: "",
  fantasy_name:"",
  username: ""
};

const formatPostalCode = (value: string) => {
  const digitsOnly = value.replace(/\D/g, "").slice(0, 8);
  return digitsOnly.replace(/^(\d{5})(\d)/, "$1-$2");
};

const formatCNPJ = (value: string) => {
  let cnpj = value.replace(/\D/g, "").slice(0, 14);
  cnpj = cnpj.replace(/^(\d{2})(\d)/, "$1.$2");
  cnpj = cnpj.replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3");
  cnpj = cnpj.replace(/\.(\d{3})(\d)/, ".$1/$2");
  return cnpj.replace(/(\d{4})(\d)/, "$1-$2");
};

const isValidFormattedCNPJ = (value: string) => /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(value);

interface InputProps {
  label?: string;
  name: keyof FormData;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  placeholder?: string;
  icon?: JSX.Element;
  disabled?: boolean;
  required?: boolean;
  fieldHint?: string;
  maxLength?: number;
  pattern?: string;
  title?: string;
}

const InputField: React.FC<InputProps> = ({
  label,
  name,
  type = "text",
  value,
  onChange,
  placeholder,
  icon,
  disabled = false,
  required = false,
  fieldHint,
  maxLength,
  pattern,
  title
}) => (
  <div className="auth-input-group">
    {label && (
      <label htmlFor={name}>
        <span>{label}</span>
        {fieldHint && (
          <span
            className={`auth-field-meta ${required ? "auth-field-meta--required" : "auth-field-meta--optional"}`}
          >
            {fieldHint}
          </span>
        )}
      </label>
    )}
    <div className="auth-input-shell">
      {icon && <span>{icon}</span>}
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        maxLength={maxLength}
        pattern={pattern}
        title={title}
      />
    </div>
  </div>
);

const RegisterForm = ({ formData, setFormData, onSubmit, toggleForm, logoFile, setLogoFile }: any) => {
  const [showPassword, setShowPassword] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!logoFile) {
      setLogoPreview(null);
      return;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.warning("A logo deve ser um arquivo de imagem.");
        e.target.value = "";
        return;
      }
      if (file.size > 3 * 1024 * 1024) {
        toast.warning("A logo deve ter no máximo 3 MB.");
        e.target.value = "";
        return;
      }
    }
    setLogoFile(file);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev: FormData) => ({ ...prev, [name]: value }));
  };

  const maskCNPJ = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev: FormData) => ({
      ...prev,
      cnpj: formatCNPJ(e.target.value)
    }));
  };

  const handlePostalCode = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, "").slice(0, 8);
    setFormData((prev: FormData) => ({
      ...prev,
      postal_code: formatPostalCode(rawValue)
    }));

    if (rawValue.length === 8) {
      const toastId = toast.info("Buscando CEP...", { autoClose: false, closeButton: false });
      try {
        const apiClient = Zip_Code_Api(rawValue);
        const response = await apiClient.get("");
        if (response.data.erro) throw new Error("CEP não encontrado");

        const { bairro, uf, localidade, logradouro } = response.data;
        setFormData((prev: FormData) => ({
          ...prev,
          neighborhood: bairro,
          state: uf,
          city: localidade,
          address: logradouro,
        }));
        toast.success("Endereço encontrado ✅", { autoClose: 2000 });
      } catch (error: any) {
        toast.update(toastId, { render: error.message || "Erro ao buscar CEP", type: "error", autoClose: 2000 });
      } finally {
        toast.dismiss(toastId);
      }
    }
  };

  return (
    <div className="w-100">
      <span className="auth-kicker">Cadastro</span>
      <h2 className="auth-heading">Área de cadastro</h2>
      <p className="auth-copy">
        Informe os dados abaixo para cadastrar sua empresa e criar seu acesso.
      </p>

      <div className="text-center mb-4">
        <img src={trackEpiLogo} alt="Logo Track EPI" className="auth-brand-logo logo-3d" />
      </div>

      <form onSubmit={onSubmit}>
        <div className="auth-input-group mb-3">
          <label htmlFor="submaster_logo_url">
            <span>Logo da empresa</span>
            <span className="auth-field-meta auth-field-meta--optional">Opcional</span>
          </label>
          <div className="d-flex align-items-center gap-3">
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: 12,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#f5f7fb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: "0 0 auto",
              }}
            >
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="Prévia da logo"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : (
                <i className="bi bi-image" style={{ fontSize: 24, opacity: 0.5 }}></i>
              )}
            </div>
            <div className="flex-grow-1">
              <input
                type="file"
                id="submaster_logo_url"
                name="submaster_logo_url"
                accept="image/*"
                onChange={handleLogoChange}
                className="form-control"
              />
              <small style={{ display: "block", marginTop: 4, opacity: 0.7 }}>
                Aparecerá nos relatórios da empresa. PNG ou JPG, até 3 MB.
              </small>
            </div>
          </div>
        </div>

        <div className="row g-3 auth-register-grid">
          <div className="col-md-6">
            <InputField
              label="Nome fantasia"
              name="fantasy_name"
              type="text"
              value={formData.fantasy_name}
              onChange={handleChange}
              placeholder="Nome fantasia"
              icon={<i className="bi bi-buildings"></i>}
              fieldHint="Opcional"
            />
          </div>
          <div className="col-md-6">
            <InputField
              label="CNPJ"
              name="cnpj"
              value={formData.cnpj}
              onChange={maskCNPJ}
              placeholder="CNPJ"
              icon={<i className="bi bi-file-earmark-text-fill"></i>}
              required
              fieldHint="Obrigatório"
              maxLength={18}
              pattern="\d{2}\.\d{3}\.\d{3}/\d{4}-\d{2}"
              title="Informe o CNPJ no formato xx.xxx.xxx/xxxx-xx"
            />
          </div>
          <div className="col-md-6">
            <InputField
              label="Razão social"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Razão social da empresa"
              icon={<i className="bi bi-person-fill"></i>}
              fieldHint="Opcional"
            />
          </div>
          <div className="col-md-6">
            <InputField
              label="E-mail corporativo"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email corporativo"
              icon={<i className="bi bi-envelope-at-fill"></i>}
              required
              fieldHint="Obrigatório"
            />
          </div>
          <div className="col-md-6">
            <InputField
              label="CEP"
              name="postal_code"
              value={formData.postal_code}
              onChange={handlePostalCode}
              placeholder="CEP"
              icon={<i className="bi bi-geo-alt-fill"></i>}
              fieldHint="Opcional"
            />
          </div>
          <div className="col-md-6">
            <InputField
              label="Endereço"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Endereço"
              icon={<i className="bi bi-house-fill"></i>}
              fieldHint="Opcional"
            />
          </div>
          <div className="col-md-6">
            <InputField
              label="Número"
              name="number_house"
              value={formData.number_house}
              onChange={handleChange}
              placeholder="Número"
              icon={<i className="bi bi-door-closed-fill"></i>}
              fieldHint="Opcional"
            />
          </div>
          <div className="col-md-6">
            <InputField
              label="Bairro"
              name="neighborhood"
              value={formData.neighborhood}
              onChange={handleChange}
              placeholder="Bairro"
              icon={<i className="bi bi-building"></i>}
              fieldHint="Opcional"
            />
          </div>
          <div className="col-md-6">
            <InputField
              label="Cidade"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="Cidade"
              icon={<i className="bi bi-geo-fill"></i>}
              fieldHint="Opcional"
            />
          </div>
          <div className="col-md-6">
            <InputField
              label="Estado"
              name="state"
              value={formData.state}
              onChange={handleChange}
              placeholder="Estado"
              icon={<i className="bi bi-flag-fill"></i>}
              fieldHint="Opcional"
            />
          </div>
          <div className="col-md-6">
            <div className="auth-input-group">
              <label htmlFor="password-register">
                <span>Senha</span>
                <span className="auth-field-meta auth-field-meta--required">
                  Obrigatório
                </span>
              </label>
              <div className="auth-input-shell auth-input-shell--with-action">
                <span>
                  <i className="bi bi-lock-fill"></i>
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password-register"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Senha segura"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="auth-input-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  aria-pressed={showPassword}
                  title={showPassword ? "Ocultar senha" : "Mostrar senha"}
                >
                  <i className={`bi ${showPassword ? "bi-eye-slash-fill" : "bi-eye-fill"}`}></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <button type="submit" className="auth-submit">
            Cadastrar-se
          </button>
        </div>
      </form>

      <button type="button" className="auth-toggle auth-link-button" onClick={toggleForm}>
        Já possui conta? Entrar
      </button>
    </div>
  );
};

type LoginUserOption = { username: string; name?: string | null; isMaster?: boolean };

const LoginForm = ({
  formData,
  setFormData,
  onSubmit,
  toggleForm,
  loginStep,
  requiresUser,
  users,
  onBack,
}: any) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: FormData) => ({ ...prev, [name]: value }));
  };

  const maskCNPJ = (e: React.ChangeEvent<HTMLInputElement>) => {
    let cnpj = e.target.value.replace(/\D/g, "");
    cnpj = cnpj.replace(/^(\d{2})(\d)/, "$1.$2")
               .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
               .replace(/\.(\d{3})(\d)/, ".$1/$2")
               .replace(/(\d{4})(\d)/, "$1-$2");
    setFormData((prev: FormData) => ({ ...prev, cnpj }));
  };

  return (
    <div className="w-100">
      <span className="auth-kicker">Login</span>
      <h2 className="auth-heading">Entrar no painel</h2>
      <p className="auth-copy">
        {loginStep === "company"
          ? "Primeiro, informe o CNPJ da empresa."
          : requiresUser
            ? "Agora escolha o usuário e informe a senha."
            : "Esta empresa ainda não possui usuários. Informe a senha da empresa."}
      </p>
      <form onSubmit={onSubmit}>
        <InputField
          label="CNPJ"
          name="cnpj"
          value={formData.cnpj}
          onChange={maskCNPJ}
          placeholder="CNPJ"
          icon={<i className="bi bi-file-earmark-text-fill"></i>}
          disabled={loginStep !== "company"}
        />
        {loginStep === "credentials" && requiresUser && (
          <div className="auth-input-group">
            <label htmlFor="username"><span>Usuário</span></label>
            <div className="auth-input-shell">
              <span><i className="bi bi-person-fill"></i></span>
              <select
                id="username"
                name="username"
                value={formData.username}
                onChange={(event) => setFormData((prev: FormData) => ({
                  ...prev,
                  username: event.target.value,
                }))}
                className="form-select border-0 shadow-none"
                required
              >
                <option value="">Selecione o usuário</option>
                {(users as LoginUserOption[]).map((user) => (
                  <option key={user.username} value={user.username}>
                    {user.name || user.username}{user.isMaster ? " (master)" : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
        {loginStep === "credentials" && <div className="auth-input-group">
          <label htmlFor="password">
            <span>Senha</span>
          </label>
          <div className="auth-input-shell auth-input-shell--with-action">
            <span>
              <i className="bi bi-lock-fill"></i>
            </span>
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Senha"
              autoComplete="current-password"
            />
            <button
              type="button"
              className="auth-input-toggle"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              aria-pressed={showPassword}
              title={showPassword ? "Ocultar senha" : "Mostrar senha"}
            >
              <i className={`bi ${showPassword ? "bi-eye-slash-fill" : "bi-eye-fill"}`}></i>
            </button>
          </div>
        </div>}
        <div className="mt-4">
          <button type="submit" className="auth-submit">
            {loginStep === "company" ? "Continuar" : "Entrar"}
          </button>
          {loginStep === "credentials" && (
            <button type="button" className="auth-toggle auth-link-button mt-2" onClick={onBack}>
              Alterar CNPJ
            </button>
          )}
        </div>
      </form>

      <button type="button" className="auth-toggle auth-link-button" onClick={toggleForm}>
        Ainda não tem conta? Cadastrar-se
      </button>
    </div>
  );
};

const AuthenticationSubmasterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formData, setFormData] = useState<FormData>({ ...defaultFormData });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [animationOffset, setAnimationOffset] = useState(0);
  const [loginStep, setLoginStep] = useState<"company" | "credentials">("company");
  const [requiresUser, setRequiresUser] = useState(false);
  const [loginUsers, setLoginUsers] = useState<LoginUserOption[]>([]);
  const mode = searchParams.get("mode");
  const isRegistering = mode === "register";

  useEffect(() => {
    if (mode !== "login" && mode !== "register") {
      setSearchParams({ mode: "login" }, { replace: true });
    }
  }, [mode, setSearchParams]);

  const toggleForm = () => {
    setLoginStep("company");
    setRequiresUser(false);
    setLoginUsers([]);
    setSearchParams({ mode: isRegistering ? "login" : "register" });
  };

  const handleRegister = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.email || !formData.password || !formData.cnpj) {
      return toast.warning("Preencha todos os campos obrigatórios!");
    }

    if (!isValidFormattedCNPJ(formData.cnpj)) {
      return toast.warning("Informe o CNPJ no formato xx.xxx.xxx/xxxx-xx");
    }

    try {
      const apiClient = setupAPIclient();
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        // A logo é um arquivo enviado separadamente (mesmo nome de campo esperado pelo multer).
        if (key === "submaster_logo_url") {
          return;
        }

        if (key === "postal_code" && typeof value === "string") {
          data.append(key, value.replace(/\D/g, ""));
          return;
        }

        data.append(key, value as string);
      });

      if (logoFile) {
        data.append("submaster_logo_url", logoFile);
      }

      data.append("access_permission_request", "true");
      data.append("masterId", "1");

      const response = await apiClient.post("/register_submaster", data, { headers: { "Content-Type": "multipart/form-data" } });
      if (response.data.has_data) {
        toast.success("Empresa cadastrada! O usuário master foi criado com a mesma senha.");
        setFormData({ ...defaultFormData });
        setLogoFile(null);
        setSearchParams({ mode: "login" });
      } else {
        toast.error("Usuário já cadastrado! ❌");
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao cadastrar. 😵");
    }
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const apiClient = setupAPIclient();
      if (loginStep === "company") {
        if (!isValidFormattedCNPJ(formData.cnpj)) {
          return toast.warning("Informe o CNPJ no formato xx.xxx.xxx/xxxx-xx");
        }
        const modeResponse = await apiClient.post("/submaster_login_mode", {
          cnpj: formData.cnpj,
        });
        if (!modeResponse?.data?.res) {
          return toast.error(modeResponse?.data?.message || "Empresa não encontrada.");
        }
        const nextRequiresUser = modeResponse.data.requiresUser === true;
        const nextUsers = Array.isArray(modeResponse.data.users) ? modeResponse.data.users : [];
        setRequiresUser(nextRequiresUser);
        setLoginUsers(nextUsers);
        setFormData((previous: FormData) => ({
          ...previous,
          username: nextUsers.length === 1 ? nextUsers[0].username : "",
          password: "",
        }));
        setLoginStep("credentials");
        return;
      }

      if (requiresUser && !formData.username) return toast.warning("Selecione o usuário.");
      if (!formData.password) return toast.warning("Informe a senha.");
      const response = await apiClient.post("/login_submaster", {
        password: formData.password,
        cnpj: formData.cnpj,
        username: requiresUser ? formData.username : undefined,
      });

      if (response.data.error) {
        if (response.data.message) toast.error(response.data.message);
        else if (!response.data.hash_error && response.data.login_error) toast.error("Usuário não encontrado! ❌");
        else if (response.data.hash_error) toast.error("Senha incorreta! ❌");
      } else {
        const storage = new LocalStorage();
        storage.saveAuthToken(
          response.data.token,
          response.data.name,
          response.data.email,
          response.data.id,
          response.data.submaster_logo_url,
          response.data.web_user
        );
        navigate("/HomeSubmasterPage");
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao tentar fazer login. ❌");
    }
  };

  useEffect(() => {
    const interval = setInterval(() => setAnimationOffset(prev => (prev + 2) % 200), 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="auth-shell d-flex align-items-center justify-content-center">
      <div className="auth-panel container-fluid p-0">
        <div className="row g-0 min-vh-100">
          <div className="col-lg-6 order-2 order-lg-1">
            <div className="auth-form-wrap d-flex align-items-center h-100">
              <div className="w-100">
                <div className="auth-form-card">
                  {isRegistering ? (
                    <RegisterForm
                      formData={formData}
                      setFormData={setFormData}
                      onSubmit={handleRegister}
                      toggleForm={toggleForm}
                      logoFile={logoFile}
                      setLogoFile={setLogoFile}
                    />
                  ) : (
                    <LoginForm
                      formData={formData}
                      setFormData={setFormData}
                      onSubmit={handleLogin}
                      toggleForm={toggleForm}
                      loginStep={loginStep}
                      requiresUser={requiresUser}
                      users={loginUsers}
                      onBack={() => {
                        setLoginStep("company");
                        setRequiresUser(false);
                        setLoginUsers([]);
                        setFormData((previous) => ({ ...previous, username: "", password: "" }));
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6 order-1 order-lg-2">
            <div className="auth-brand d-flex flex-column justify-content-between position-relative overflow-hidden">
              <div>
                <span className="auth-kicker auth-brand-badge">
                  Plataforma Track EPI
                </span>
                <img src={trackEpiLogo} alt="Logo Track EPI" className="auth-brand-logo auth-brand-logo--large logo-3d mt-4" />
                <h1>Controle de EPI</h1>
                <p>
                  Acesse os módulos de funcionários, usuários, documentos e EPIs
                  em uma interface mais clara, organizada e adaptada para mobile.
                </p>
              </div>

              <div className="auth-brand__stats">
                <div>
                  <strong>Usuários</strong>
                  <span>Gestão centralizada</span>
                </div>
                <div>
                  <strong>Documentos</strong>
                  <span>Consulta e emissão</span>
                </div>
                <div>
                  <strong>EPIs</strong>
                  <span>Controle operacional</span>
                </div>
              </div>

              <div
                className="auth-animated-line"
                style={{ transform: `translateX(${animationOffset - 200}px)` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes logo3DEffect {
          0% { transform: rotateY(0deg) scale(1); filter: drop-shadow(0 0 2px #fff); }
          50% { transform: rotateY(15deg) scale(1.05); filter: drop-shadow(0 0 12px #fff); }
          100% { transform: rotateY(0deg) scale(1); filter: drop-shadow(0 0 2px #fff); }
        }
        .logo-3d {
          animation: logo3DEffect 3s infinite;
          transition: transform 0.3s;
        }
      `}</style>
    </div>
  );
};

export default AuthenticationSubmasterPage;
